/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902051366 — calculateDiscountedAmount */
/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902411790 — Заокруглення та оплата (roundSubunitsForPayment) */

import { SUBUNITS_IN_CURRENCY } from '../../shared/constants';

const DECIMAL_INPUT_REGEX = /^\d*\.?\d{0,2}$/;

export interface DecimalInputResult {
    display: string;
    value: number;
}

/**
 * Normalizes a decimal input value: replaces comma with dot,
 * prepends 0 for leading dot, validates format (max 2 decimals).
 * Returns the cleaned display string AND parsed number, or null if invalid.
 * The display string preserves in-progress input like "1." or "1.50" so the
 * caller can render it verbatim without losing trailing dots/zeros.
 */
export function normalizeDecimalInput(value: string): DecimalInputResult | null {
    const dotValue = value.replace(',', '.');
    const normalized = dotValue.startsWith('.') ? '0' + dotValue : dotValue;
    if (!DECIMAL_INPUT_REGEX.test(normalized)) {
        return null;
    }
    return {
        display: normalized,
        value: Number(normalized) || 0,
    };
}

/**
 * Builds a localized error message for amount validation.
 */
export function buildAmountError(
    t: (key: string) => string,
    limit: number,
    currencyCode: string,
    type: 'max' | 'min',
): string {
    const message = type === 'max' ? 'The entered amount is too big.' : 'The entered amount is too small.';
    const label = type === 'max' ? 'Max amount is' : 'Min amount is';
    return `${t(message)} ${t(label)} ${limit} ${t(`Currency.${currencyCode}`)}`;
}

/**
 * Calculates order amount in subunits, applying optional campaign discount.
 */
export function calculateDiscountedAmount(
    amount: number,
    discountPercentage?: number | null,
): number {
    const discount = discountPercentage ? 1 - discountPercentage / 100 : 1;
    return amount * SUBUNITS_IN_CURRENCY * discount;
}

/**
 * Snaps a (potentially fractional) subunit value to integer cents.
 *
 * Rule: examine the 3rd decimal of the currency value (= 1st decimal of the
 * subunit value) — if ≥ 5 round up (ceil), otherwise truncate (floor).
 * Decimals past the 3rd of the currency value are intentionally ignored, so
 * e.g. 1.9418 → 1.94 (not 1.95 as standard rounding would produce).
 *
 * Applied at the FE→backend boundary in useOrderAmount.submitAmount so the
 * stored order.amount is always a clean integer subunit value.
 */
export function roundSubunitsForPayment(subunits: number): number {
    // NaN / Infinity → 0 so a poisoned value never reaches submitAmount / DB.
    if (!isFinite(subunits)) return 0;

    // toFixed(10) collapses IEEE-754 noise — 2.3 * 100 = 229.99999999999997
    // becomes "230.0000000000", and 100.00500000000001 becomes "100.0050000000".
    // We must floor/ceil the cleaned value, not the raw input — otherwise the
    // detected "no significant fraction" branch still floors the shadow-under
    // bits (229.999...997 → 229 instead of 230).
    const str = subunits.toFixed(10);
    const dot = str.indexOf('.');
    const firstFractionDigit = dot >= 0 ? Number(str.charAt(dot + 1)) : 0;
    const cleaned = Number(str);

    return firstFractionDigit >= 5 ? Math.ceil(cleaned) : Math.floor(cleaned);
}

/**
 * Whether an amount may be persisted as the order's price.
 *
 * Zero is a screen's initial state, never a user's intent: every amount screen
 * submits from an effect that also runs on mount, and `roundSubunitsForPayment`
 * maps poisoned values to zero. The stored amount is what actually gets charged
 * — the commit reads it back from the row and hands it to the PSP — so a zero
 * must never reach it (SWRN-1964).
 */
export function isSubmittableAmount(subunits: number): boolean {
    return Number.isInteger(subunits) && subunits > 0;
}
